import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ArrowLeft, ArrowRight, BarChart3, Clock3, FileText, MessageSquareMore, Users } from 'lucide-react';
import { apiClient, API_ENDPOINTS } from '../config/api';

type UsageHistoryPage = 'home' | 'summary' | 'users' | 'detail';
type SummaryPeriod = 'week' | 'month';

interface TopRow {
  label: string;
  count: number;
}

interface SummaryResponse {
  period: SummaryPeriod;
  periodLabel: string;
  rangeStart: string;
  rangeEnd: string;
  cards: {
    files: TopRow[];
    views: TopRow[];
    yearRanges: TopRow[];
    contractorSelections: TopRow[];
    contractorInputs: TopRow[];
    departments: TopRow[];
    businesses: TopRow[];
    workTypes: TopRow[];
  };
  chatMessages: Array<{
    id: number;
    username: string | null;
    fileName: string | null;
    message: string;
    createdAt: string | null;
  }>;
}

interface UserRow {
  id: number;
  username: string;
  isActive?: boolean;
  lastActivityAt: string | null;
  eventCount: number;
}

interface UsageUsersResponse {
  users: UserRow[];
}

interface ActivityRow {
  date: string;
  label: string;
  fileSelections: Array<{ time: string; fileName: string | null }>;
  filterActions: Array<{
    time: string;
    triggerAction: string | null;
    viewName: string | null;
    yearRange: string | null;
    contractorName: string | null;
    contractorInput: string | null;
    departments: string[];
    businesses: string[];
    workTypes: string[];
  }>;
  chatMessages: Array<{ time: string; message: string | null; fileName: string | null }>;
}

interface UsageDetailResponse {
  user: {
    id: number;
    username: string;
  };
  rangeStart: string;
  rangeEnd: string;
  rows: ActivityRow[];
}

interface UsageHistoryDashboardProps {
  onBackToDeveloper: () => void;
}

const extractErrorMessage = (err: unknown, fallback: string) => {
  if (axios.isAxiosError(err)) {
    return err.response?.data?.error?.message || fallback;
  }
  return err instanceof Error ? err.message : fallback;
};

const formatDateTime = (value?: string | null) => {
  if (!value) {
    return '未記録';
  }
  const normalized = /z$|[+-]\d{2}:\d{2}$/i.test(value) ? value : `${value}Z`;
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) {
    return '未記録';
  }
  return parsed.toLocaleString('ja-JP', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'Asia/Tokyo',
  });
};

const parseDateOnly = (value?: string | null) => {
  if (!value) {
    return null;
  }
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed;
};

const formatDateWithWeekday = (value?: string | null) => {
  const parsed = parseDateOnly(value);
  if (!parsed) {
    return value || '未記録';
  }
  return parsed.toLocaleDateString('ja-JP', {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    weekday: 'short',
  });
};

const startOfDay = (value: Date) => new Date(value.getFullYear(), value.getMonth(), value.getDate());

const formatDateKey = (value: Date) => {
  const year = value.getFullYear();
  const month = `${value.getMonth() + 1}`.padStart(2, '0');
  const day = `${value.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const createDefaultWeekStart = () => {
  const today = startOfDay(new Date());
  today.setDate(today.getDate() - 6);
  return formatDateKey(today);
};

const UsageHistoryDashboard: React.FC<UsageHistoryDashboardProps> = ({ onBackToDeveloper }) => {
  const [page, setPage] = useState<UsageHistoryPage>('home');
  const [period, setPeriod] = useState<SummaryPeriod>('week');
  const [summary, setSummary] = useState<SummaryResponse | null>(null);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [detail, setDetail] = useState<UsageDetailResponse | null>(null);
  const [selectedUser, setSelectedUser] = useState<UserRow | null>(null);
  const [rangeStart, setRangeStart] = useState(createDefaultWeekStart);
  const [modalOpen, setModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSummary = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get<SummaryResponse>(API_ENDPOINTS.adminUsageHistorySummary, {
        params: { period },
      });
      setSummary(response.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, '利用履歴の集計取得に失敗しました。'));
    } finally {
      setIsLoading(false);
    }
  }, [period]);

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get<UsageUsersResponse>(API_ENDPOINTS.adminUsageHistoryUsers);
      setUsers(response.data.users || []);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, '一般ユーザ一覧の取得に失敗しました。'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  const fetchDetail = useCallback(async (user: UserRow, nextRangeStart?: string) => {
    setIsLoading(true);
    try {
      const effectiveRangeStart = nextRangeStart || rangeStart;
      const response = await apiClient.get<UsageDetailResponse>(API_ENDPOINTS.adminUsageHistoryUserDetail(user.id), {
        params: { rangeStart: effectiveRangeStart },
      });
      setDetail(response.data);
      setSelectedUser(user);
      setPage('detail');
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err, 'ユーザ別の作業履歴取得に失敗しました。'));
    } finally {
      setIsLoading(false);
    }
  }, [rangeStart]);

  useEffect(() => {
    if (page === 'summary') {
      fetchSummary();
    }
  }, [page, fetchSummary]);

  useEffect(() => {
    if (page === 'users') {
      fetchUsers();
    }
  }, [page, fetchUsers]);

  useEffect(() => {
    if (page === 'detail' && selectedUser) {
      fetchDetail(selectedUser, rangeStart);
    }
  }, [page, rangeStart, selectedUser, fetchDetail]);

  const summaryCards = useMemo(() => ([
    { key: 'files', title: '1) 分析したファイル', rows: summary?.cards.files || [] },
    { key: 'views', title: '2) 押したボタン', rows: summary?.cards.views || [] },
    { key: 'years', title: '3) 年度の開始 / 終了', rows: summary?.cards.yearRanges || [] },
    { key: 'contractorSelections', title: '4) 落札者の選択', rows: summary?.cards.contractorSelections || [] },
    { key: 'contractorInputs', title: '4) 落札者の自由入力', rows: summary?.cards.contractorInputs || [] },
    { key: 'departments', title: '5) 部局', rows: summary?.cards.departments || [] },
    { key: 'businesses', title: '6) 事業', rows: summary?.cards.businesses || [] },
    { key: 'workTypes', title: '7) 工種', rows: summary?.cards.workTypes || [] },
  ]), [summary]);

  const canMoveNext = useMemo(() => {
    const maxStart = startOfDay(new Date());
    maxStart.setDate(maxStart.getDate() - 6);
    return new Date(`${rangeStart}T00:00:00`).getTime() < maxStart.getTime();
  }, [rangeStart]);

  const visibleDetailRows = useMemo(() => {
    if (!detail) {
      return [];
    }
    return detail.rows.filter((row) => (
      row.fileSelections.length > 0 ||
      row.filterActions.length > 0 ||
      row.chatMessages.length > 0
    ));
  }, [detail]);

  const handleMoveRange = (days: number) => {
    const next = new Date(`${rangeStart}T00:00:00`);
    next.setDate(next.getDate() + days);
    setRangeStart(formatDateKey(next));
  };

  const renderTopList = (rows: TopRow[]) => {
    if (!rows.length) {
      return <p className="usage-history-empty">対象期間のデータはありません。</p>;
    }
    return (
      <ol className="usage-top-list">
        {rows.map((row) => (
          <li key={`${row.label}-${row.count}`} className="usage-top-item">
            <span>{row.label}</span>
            <strong>{row.count}件</strong>
          </li>
        ))}
      </ol>
    );
  };

  if (isLoading && page !== 'home') {
    return (
      <div className="file-manager-loading">
        <div className="loading-spinner"></div>
        <p>ユーザの利用履歴を読み込み中...</p>
      </div>
    );
  }

  return (
    <div className="usage-history-page">
      <section className="usage-history-hero">
        <div>
          <span className="login-activity-eyebrow">Admin Visibility</span>
          <h2>ユーザの利用履歴</h2>
          <p>一般ユーザがどのファイルを分析し、どの条件で再分析やチャットを実行したかを確認できます。</p>
        </div>
        <button className="login-activity-back-button" onClick={page === 'home' ? onBackToDeveloper : () => setPage('home')}>
          <ArrowLeft size={16} />
          {page === 'home' ? '管理者コンソールに戻る' : '利用履歴トップに戻る'}
        </button>
      </section>

      {error && (
        <div className="error-message">
          <span>{error}</span>
        </div>
      )}

      {page === 'home' && (
        <section className="usage-history-home-grid">
          <button className="usage-history-entry-card" onClick={() => setPage('summary')}>
            <BarChart3 size={24} />
            <div>
              <h3>すべてのデータの集計</h3>
              <p>直近1週間 / 1か月のトップ10と AI チャット入力一覧を確認します。</p>
            </div>
          </button>
          <button className="usage-history-entry-card" onClick={() => setPage('users')}>
            <Users size={24} />
            <div>
              <h3>ユーザ毎の集計</h3>
              <p>一般ユーザ一覧から、1週間単位の日別作業履歴を確認します。</p>
            </div>
          </button>
        </section>
      )}

      {page === 'summary' && summary && (
        <>
          <section className="usage-history-toolbar">
            <div>
              <h3>すべてのデータの集計</h3>
              <p>{formatDateWithWeekday(summary.rangeStart)} から {formatDateWithWeekday(summary.rangeEnd)} までの {summary.periodLabel} を表示しています。</p>
            </div>
            <div className="login-activity-toggle-group">
              <button
                className={`login-toggle-button ${period === 'week' ? 'active' : ''}`}
                onClick={() => setPeriod('week')}
              >
                1週間毎
              </button>
              <button
                className={`login-toggle-button ${period === 'month' ? 'active' : ''}`}
                onClick={() => setPeriod('month')}
              >
                1か月毎
              </button>
            </div>
          </section>

          <section className="usage-summary-grid">
            {summaryCards.map((card) => (
              <article key={card.key} className="usage-summary-card">
                <h4>{card.title}</h4>
                {renderTopList(card.rows)}
              </article>
            ))}
            <article className="usage-summary-card usage-summary-card-accent">
              <div className="usage-summary-card-header">
                <div>
                  <h4>8) AIチャットの入力内容</h4>
                  <p>{summary.chatMessages.length}件</p>
                </div>
                <button className="reanalyze-button" onClick={() => setModalOpen(true)}>
                  一覧を表示
                </button>
              </div>
              <p className="usage-history-note">モーダル内で全件をスクロール表示します。</p>
            </article>
          </section>
        </>
      )}

      {page === 'users' && (
        <section className="login-activity-panel">
          <div className="login-activity-panel-header">
            <div>
              <h3>一般ユーザ一覧</h3>
              <p>「作業履歴」から 1 週間単位の日別履歴を表示します。</p>
            </div>
          </div>
          <div className="login-activity-table-wrap">
            <table className="login-activity-table">
              <thead>
                <tr>
                  <th>ユーザ名</th>
                  <th>状態</th>
                  <th>最終利用</th>
                  <th>記録件数</th>
                  <th>操作</th>
                </tr>
              </thead>
              <tbody>
                {users.length ? (
                  users.map((user) => (
                    <tr key={user.id}>
                      <td>{user.username}</td>
                      <td>{user.isActive === false ? '停止中' : '有効'}</td>
                      <td>{formatDateTime(user.lastActivityAt)}</td>
                      <td>{user.eventCount}件</td>
                      <td>
                        <button
                          className="reanalyze-button"
                          style={{ padding: '8px 12px' }}
                          onClick={() => fetchDetail(user)}
                        >
                          作業履歴
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={5} className="login-activity-empty-cell">一般ユーザが見つかりません。</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {page === 'detail' && detail && selectedUser && (
        <>
          <section className="usage-history-toolbar">
            <div>
              <h3>{selectedUser.username} の作業履歴</h3>
              <p>{formatDateWithWeekday(detail.rangeStart)} から {formatDateWithWeekday(detail.rangeEnd)} までの 7 日分を表示します。</p>
            </div>
            <div className="login-activity-range-controls">
              <button className="login-range-button" onClick={() => handleMoveRange(-7)}>
                <ArrowLeft size={16} />
                前の7日間
              </button>
              <button className="login-range-button" onClick={() => handleMoveRange(7)} disabled={!canMoveNext}>
                次の7日間
                <ArrowRight size={16} />
              </button>
            </div>
          </section>

          <section className="usage-detail-grid">
            {visibleDetailRows.length ? visibleDetailRows.map((row) => (
              <article key={row.date} className="usage-day-card">
                <div className="usage-day-card-header">
                  <div>
                    <h4>{formatDateWithWeekday(row.date)}</h4>
                    <p>{row.label}</p>
                  </div>
                  <Clock3 size={18} />
                </div>

                <div className="usage-day-section">
                  <h5><FileText size={14} /> 1) 分析したファイル</h5>
                  {row.fileSelections.length ? row.fileSelections.map((item, index) => (
                    <div key={`${row.date}-file-${index}`} className="usage-day-entry">{item.time} {item.fileName || '未記録'}</div>
                  )) : <div className="usage-day-empty">なし</div>}
                </div>

                <div className="usage-day-section">
                  <h5><BarChart3 size={14} /> 2) から 7) の条件操作</h5>
                  {row.filterActions.length ? row.filterActions.map((item, index) => (
                    <div key={`${row.date}-filter-${index}`} className="usage-day-entry">
                      <strong>{item.time}</strong> {item.triggerAction === 'chat_with_filters' ? 'この条件でチャット' : '再分析'}
                      <span> / ボタン: {item.viewName || '未選択'}</span>
                      <span> / 年度: {item.yearRange || '指定なし'}</span>
                      <span> / 落札者選択: {item.contractorName || 'なし'}</span>
                      <span> / 落札者入力: {item.contractorInput || 'なし'}</span>
                      <span> / 部局: {item.departments.join('、') || 'なし'}</span>
                      <span> / 事業: {item.businesses.join('、') || 'なし'}</span>
                      <span> / 工種: {item.workTypes.join('、') || 'なし'}</span>
                    </div>
                  )) : <div className="usage-day-empty">なし</div>}
                </div>

                <div className="usage-day-section">
                  <h5><MessageSquareMore size={14} /> 8) AIチャット入力</h5>
                  {row.chatMessages.length ? row.chatMessages.map((item, index) => (
                    <div key={`${row.date}-chat-${index}`} className="usage-day-entry">
                      <strong>{item.time}</strong> {item.fileName ? `(${item.fileName}) ` : ''}{item.message || ''}
                    </div>
                  )) : <div className="usage-day-empty">なし</div>}
                </div>
              </article>
            )) : (
              <article className="usage-day-card">
                <div className="usage-day-card-header">
                  <div>
                    <h4>表示対象なし</h4>
                    <p>{formatDateWithWeekday(detail.rangeStart)} から {formatDateWithWeekday(detail.rangeEnd)}</p>
                  </div>
                  <Clock3 size={18} />
                </div>
                <div className="usage-day-empty">この期間の作業履歴はありません。</div>
              </article>
            )}
          </section>
        </>
      )}

      {modalOpen && summary && (
        <div className="usage-modal-backdrop" onClick={() => setModalOpen(false)}>
          <div className="usage-modal" onClick={(event) => event.stopPropagation()}>
            <div className="usage-modal-header">
              <div>
                <h3>AIチャットの入力内容</h3>
                <p>{formatDateWithWeekday(summary.rangeStart)} から {formatDateWithWeekday(summary.rangeEnd)} まで</p>
              </div>
              <button className="login-activity-back-button" onClick={() => setModalOpen(false)}>
                <ArrowLeft size={16} />
                閉じる
              </button>
            </div>
            <div className="usage-modal-body">
              {summary.chatMessages.length ? summary.chatMessages.map((item) => (
                <article key={item.id} className="usage-modal-message">
                  <div className="usage-modal-message-meta">
                    <strong>{item.username || '不明ユーザ'}</strong>
                    <span>{item.fileName || 'ファイル未記録'}</span>
                    <span>{formatDateTime(item.createdAt)}</span>
                  </div>
                  <p>{item.message}</p>
                </article>
              )) : (
                <p className="usage-history-empty">対象期間のチャット入力はありません。</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsageHistoryDashboard;
